import {
  createContext,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { IconAlert } from "../components/IceBlueGlyphs";
import { acquireBodyScrollLock } from "../lib/bodyScrollLock";
import "../ice-blue-shell.css";

/** A step that throws work away, asked about before it runs. */
export interface DiscardConfirmation {
  readonly title: string;
  /** What goes, one line each. */
  readonly discards: readonly string[];
  /** What stays, when the physician might expect it to go too. */
  readonly keeps?: string;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
}

/**
 * A step that goes ahead on older information, asked about before it runs. The safer path takes
 * Cancel's place and has the focus; Escape and the scrim still leave the encounter as it was.
 */
export interface ProceedConfirmation {
  readonly title: string;
  readonly detail: string;
  readonly alternativeLabel: string;
  readonly onAlternative: () => void;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
}

/** What the one dialog shows, whichever question opened it. */
export interface OpenConfirmation {
  readonly title: string;
  readonly detail: ReactNode;
  readonly secondaryLabel: string;
  /** Runs on the ghost button; Cancel only closes. */
  readonly onSecondary?: () => void;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  /** A step that cannot be undone, such as deleting a room, gets the red confirm button. */
  readonly tone?: "danger";
}

interface EncounterConfirm {
  readonly discard: (request: DiscardConfirmation) => void;
  readonly proceed: (request: ProceedConfirmation) => void;
}

const ConfirmDiscardContext = createContext<EncounterConfirm | null>(null);

function useEncounterConfirm(hook: string): EncounterConfirm {
  const confirm = useContext(ConfirmDiscardContext);
  if (confirm === null) throw new Error(`${hook} needs a ConfirmDiscardProvider`);
  return confirm;
}

/** Asks before discarding; the step runs only on the physician's confirmation. */
export function useConfirmDiscard(): (request: DiscardConfirmation) => void {
  return useEncounterConfirm("useConfirmDiscard").discard;
}

/** Asks before going ahead on older information; the physician can take the safer path instead. */
export function useConfirmProceed(): (request: ProceedConfirmation) => void {
  return useEncounterConfirm("useConfirmProceed").proceed;
}

/**
 * Hosts the one confirmation the encounter can have open. Cancel, Escape and the scrim leave the
 * encounter exactly as it was. While the surface is hidden the dialog is not mounted, but the
 * question stays until it is answered.
 */
export function ConfirmDiscardProvider({
  hidden,
  children,
}: {
  readonly hidden: boolean;
  readonly children: ReactNode;
}) {
  const [request, setRequest] = useState<OpenConfirmation | null>(null);
  const cancel = useCallback(() => setRequest(null), []);
  const confirm = useMemo<EncounterConfirm>(
    () => ({
      discard: (discard) =>
        setRequest({
          title: discard.title,
          detail: (
            <>
              <p>This throws away:</p>
              <ul>
                {discard.discards.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              {discard.keeps === undefined ? null : <p>{discard.keeps}</p>}
            </>
          ),
          secondaryLabel: "Cancel",
          confirmLabel: discard.confirmLabel,
          onConfirm: discard.onConfirm,
        }),
      proceed: (proceed) =>
        setRequest({
          title: proceed.title,
          detail: <p>{proceed.detail}</p>,
          secondaryLabel: proceed.alternativeLabel,
          onSecondary: proceed.onAlternative,
          confirmLabel: proceed.confirmLabel,
          onConfirm: proceed.onConfirm,
        }),
    }),
    [],
  );
  return (
    <ConfirmDiscardContext.Provider value={confirm}>
      {children}
      {request !== null && !hidden ? (
        <DiscardConfirmDialog
          request={request}
          onCancel={cancel}
          onSecondary={() => {
            setRequest(null);
            request.onSecondary?.();
          }}
          onConfirm={() => {
            setRequest(null);
            request.onConfirm();
          }}
        />
      ) : null}
    </ConfirmDiscardContext.Provider>
  );
}

/**
 * The Ice Blue confirm, the same look as the microphone-switch prompt, focused on its ghost button
 * (Cancel, or the safer path). Surfaces outside the encounter render it directly.
 */
export function DiscardConfirmDialog({
  request,
  onCancel,
  onSecondary,
  onConfirm,
}: {
  readonly request: OpenConfirmation;
  readonly onCancel: () => void;
  readonly onSecondary: () => void;
  readonly onConfirm: () => void;
}) {
  const titleId = useId();
  const detailId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const secondaryButtonRef = useRef<HTMLButtonElement>(null);

  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  // The dialog is modal: focus moves in as it mounts and stays inside it, and Escape cancels
  // wherever focus is (a key pressed before focus arrived used to miss the dialog).
  useLayoutEffect(() => {
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const releaseScrollLock = acquireBodyScrollLock();
    const focusInside = () => {
      const dialog = dialogRef.current;
      if (dialog !== null && !dialog.contains(document.activeElement))
        secondaryButtonRef.current?.focus();
    };
    focusInside();
    const focusTimer = window.setTimeout(focusInside, 0);
    const onWindowKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        onCancelRef.current();
        return;
      }
      // The command palette would open under the scrim and take focus out of the dialog.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    // Something behind the scrim that takes focus (a control, or a frame, which blurs the window)
    // gives it back.
    const onFocusIn = (event: FocusEvent) => {
      if (event.target instanceof Node && !dialogRef.current?.contains(event.target)) focusInside();
    };
    let blurTimer: number | undefined;
    const onWindowBlur = () => {
      window.clearTimeout(blurTimer);
      blurTimer = window.setTimeout(() => {
        if (document.activeElement instanceof HTMLIFrameElement) focusInside();
      }, 0);
    };
    window.addEventListener("keydown", onWindowKeyDown, true);
    document.addEventListener("focusin", onFocusIn, true);
    window.addEventListener("blur", onWindowBlur);
    return () => {
      window.clearTimeout(focusTimer);
      window.clearTimeout(blurTimer);
      window.removeEventListener("keydown", onWindowKeyDown, true);
      document.removeEventListener("focusin", onFocusIn, true);
      window.removeEventListener("blur", onWindowBlur);
      releaseScrollLock();
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  // Escape is handled on the window, above.
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== "Tab") return;
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>("button:not([disabled])") ?? [],
    );
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Portaled like the Horizon modals, so the encounter's own layout never clips or covers it.
  return createPortal(
    // Clicking the scrim cancels, like Escape; the target check keeps bubbled dialog clicks out.
    // biome-ignore lint/a11y/noStaticElementInteractions: scrim dismissal backs up Escape and the focused ghost button.
    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users have Escape and the focused ghost button.
    <div
      className="ib-confirm-scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <section
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={detailId}
        className="ib-confirm"
        onKeyDown={handleKeyDown}
      >
        <h2 id={titleId} className="ib-confirm-title">
          <IconAlert />
          {request.title}
        </h2>
        <div id={detailId} className="ib-confirm-detail">
          {request.detail}
        </div>
        <div className="ib-confirm-actions">
          <button
            ref={secondaryButtonRef}
            type="button"
            className="ib-confirm-btn ghost"
            onClick={onSecondary}
          >
            {request.secondaryLabel}
          </button>
          <button
            type="button"
            className={`ib-confirm-btn ${request.tone === "danger" ? "danger" : "primary"}`}
            onClick={onConfirm}
          >
            {request.confirmLabel}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
